import { PageLayout } from 'components/common';
import {
  ChatWaitlistBanner,
  ChatWidget,
  ColumnPanel,
  FilterPanel,
  Header,
  ListOrders,
  ListShipments,
  OrdersFilters,
  ShipmentsFilters,
} from 'components/core';
import { useCallback, useContext, useMemo, useState } from 'react';
import {
  CHAT_WAITLIST_STATUS,
  JOIN_WAITLIST_URL,
  WaitlistStatus,
} from 'utils/constants/Chat';
import { ColumnContext, ListContext, ViewContext } from 'utils/context';
import {
  usePanelAnimation,
  usePermissions,
  useTableTransition,
} from 'utils/hooks';

import { Flex } from '@project44/manifest-ds';
import { CarrierWelcomeBanner } from '@project44/shared-components';
import { useLocalStorage, useUser } from '@project44/ui-utils';

// Due to layout constrains from sidebar animations, we need to lock the banner
// height. Value computed from a Banner with a single line of text.
const bannerHeight = '56px';

function ResultsPage() {
  const {
    flags: { featureExc2171, featureNx726 },
    permissions: { canViewChat, canViewOrders },
  } = usePermissions();

  const { data: user } = useUser({ skipRedirectOn401: true });
  const isCarrierTenant = user?.tenantCarrierNetworkRoles.includes('CARRIER');

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  const [waitlistStatus, setWaitlistStatus] = useLocalStorage<WaitlistStatus>({
    namespace: null, // For retro-compatibility
    key: CHAT_WAITLIST_STATUS,
    defaultValue: WaitlistStatus.PENDING_JOIN,
  });

  const [carrierWelcomeVisible, setCarrierWelcomeVisible] =
    useLocalStorage<boolean>({
      namespace: null, // Also used by other webapps
      key: 'show-carrier-welcome-banner',
      defaultValue: true,
    });

  const filtersPanel = usePanelAnimation();
  const columnsPanel = usePanelAnimation();
  const listTable = useTableTransition();

  const { totalCountOrders, totalCountShipments } = useContext(ListContext);
  const { updateColumns } = useContext(ColumnContext);

  const { setWillSearch, viewType } = useContext(ViewContext);
  const isOrder = canViewOrders && viewType === 'order';

  const {
    handleCloseFilter,
    handleOpenFilter,
    handleCloseColumn,
    handleOpenColumn,
  } = useMemo(
    () => ({
      handleCloseFilter: () => {
        filtersPanel.slideOut();
        listTable.slideOut();
        setIsFilterOpen(false);
      },
      handleOpenFilter: () => {
        columnsPanel.slideOut();
        filtersPanel.slideIn();
        listTable.slideIn();
        setIsColumnOpen(false);
        setIsFilterOpen(true);
      },
      handleCloseColumn: () => {
        columnsPanel.slideOut();
        listTable.slideOut();
        setIsColumnOpen(false);
      },
      handleOpenColumn: () => {
        columnsPanel.slideIn();
        filtersPanel.slideOut();
        listTable.slideIn();
        setIsFilterOpen(false);
        setIsColumnOpen(true);
      },
    }),
    [columnsPanel, filtersPanel, listTable],
  );

  const onCarrierWelcomeBannerClose = useCallback(() => {
    setCarrierWelcomeVisible(false);
  }, [setCarrierWelcomeVisible]);

  const onChatWaitlistBannerJoin = useCallback(() => {
    setWaitlistStatus(WaitlistStatus.PENDING_GRANTED);
    window.open(JOIN_WAITLIST_URL, '_blank');
  }, [setWaitlistStatus]);

  const onChatWaitlistBannerClose = useCallback(() => {
    setWaitlistStatus(WaitlistStatus.CLOSED);
  }, [setWaitlistStatus]);

  const handleToggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
  }, [setIsChatOpen]);

  const carrierWelcomeAvailable = featureNx726 && isCarrierTenant;

  const showCarrierWelcomeBanner =
    carrierWelcomeAvailable && carrierWelcomeVisible;

  const showChatBanner =
    !canViewChat && featureExc2171 && waitlistStatus === WaitlistStatus.GRANTED;

  const showChatWaitlistBanner =
    !carrierWelcomeAvailable && // Never show GPT banner when carrier welcome is available
    !canViewChat &&
    [WaitlistStatus.PENDING_JOIN, WaitlistStatus.PENDING_GRANTED].includes(
      waitlistStatus,
    );

  const actualBannerHeight =
    showCarrierWelcomeBanner || showChatWaitlistBanner ? bannerHeight : '0px';

  const renderBanner = useCallback(() => {
    if (showCarrierWelcomeBanner) {
      return (
        <CarrierWelcomeBanner
          onClose={onCarrierWelcomeBannerClose}
          css={{ height: bannerHeight }}
        />
      );
    }
    if (showChatWaitlistBanner) {
      return (
        <ChatWaitlistBanner
          status={waitlistStatus}
          onJoin={onChatWaitlistBannerJoin}
          onClose={onChatWaitlistBannerClose}
          css={{ height: bannerHeight }}
        />
      );
    }
    return null;
  }, [
    onCarrierWelcomeBannerClose,
    onChatWaitlistBannerClose,
    onChatWaitlistBannerJoin,
    showCarrierWelcomeBanner,
    showChatWaitlistBanner,
    waitlistStatus,
  ]);

  return (
    <PageLayout renderBanner={renderBanner}>
      {canViewChat && (
        <ChatWidget
          doSearch={setWillSearch}
          isHidden={!isChatOpen}
          onClose={() => setIsChatOpen(false)}
        />
      )}
      <Header
        doSearch={setWillSearch}
        isChatOpen={isChatOpen}
        isColumnOpen={isColumnOpen}
        isFilterOpen={isFilterOpen}
        onClickChat={handleToggleChat}
        onClickColumn={isColumnOpen ? handleCloseColumn : handleOpenColumn}
        onClickFilter={isFilterOpen ? handleCloseFilter : handleOpenFilter}
        totalOrders={totalCountOrders}
        totalShipments={totalCountShipments}
      />
      <Flex css={{ height: '100%' }}>
        {isOrder ? (
          <ListOrders css={listTable.style} doSearch={setWillSearch} />
        ) : (
          <ListShipments css={listTable.style} doSearch={setWillSearch} />
        )}
        <FilterPanel
          doSearch={setWillSearch}
          filters={isOrder ? OrdersFilters : ShipmentsFilters}
          handleLaunch={handleToggleChat}
          isChatActive={isChatOpen}
          onClose={handleCloseFilter}
          showChatBanner={showChatBanner}
          bannerHeight={actualBannerHeight}
          style={filtersPanel.style}
        />
        <ColumnPanel
          isOrder={isOrder}
          onClose={handleCloseColumn}
          style={columnsPanel.style}
          bannerHeight={actualBannerHeight}
          updateColumns={updateColumns}
        />
      </Flex>
    </PageLayout>
  );
}

export default ResultsPage;
